import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ExternalLink, Leaf, Loader2, PackageCheck, Percent, Search, ShieldCheck, ShoppingBag, Star, Truck } from "lucide-react";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/3c7d4d91-69a4-4dbc-840a-7249f347f396/images/cbb264a8a10432ad73a8b28af34d54c87bf90db395510ed34784d8742cf07f32.jpeg";

const SORTS = [
  ["name", "Name A–Z"],
  ["rating", "Top rated"],
  ["price_asc", "Price: low → high"],
  ["price_desc", "Price: high → low"],
];

export default function Storefront() {
  const { addItem } = useCart();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("name");

  useEffect(() => {
    axios.get(`${API}/store/products`)
      .then(({ data }) => setProducts(data.products))
      .catch(() => setProducts([]));
    axios.get(`${API}/store/bundles`)
      .then(({ data }) => setBundles(data.bundles))
      .catch(() => {});
  }, []);

  const categories = useMemo(() => {
    if (!products) return [];
    return ["All", ...Array.from(new Set(products.map((p) => p.category).filter(Boolean))).sort()];
  }, [products]);

  const shown = useMemo(() => {
    if (!products) return [];
    let list = products.filter((p) =>
      (category === "All" || p.category === category) &&
      p.name.toLowerCase().includes(query.trim().toLowerCase()));
    if (sort === "price_asc") list = [...list].sort((a, b) => a.price_gbp - b.price_gbp);
    else if (sort === "price_desc") list = [...list].sort((a, b) => b.price_gbp - a.price_gbp);
    else if (sort === "rating") list = [...list].sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0) || (b.review_count || 0) - (a.review_count || 0));
    else list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [products, query, category, sort]);

  if (products === null) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-primary" /></div>;
  }

  return (
    <div data-testid="storefront-page">
      {/* Immersive hero */}
      <div className="relative overflow-hidden">
        <img src={HERO_IMG} alt="Apothecary table of dried herbs" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/85 via-slate-950/60 to-slate-950/30" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="inline-flex items-center gap-2 border border-emerald-400/40 bg-emerald-500/15 backdrop-blur-sm text-emerald-200 rounded-full px-4 py-1.5 text-xs font-semibold mb-5">
            <Leaf className="w-3.5 h-3.5" /> {products.length} botanicals · all in stock
          </div>
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white max-w-xl leading-tight">
            The Herb Store
          </h1>
          <p className="text-slate-200/90 mt-4 max-w-lg text-sm sm:text-base">
            Real botanicals, photographed in nature — hand-matched to your protocols and delivered to your door.
          </p>
          <div className="flex flex-wrap gap-3 mt-7 text-xs text-slate-100">
            <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-3.5 py-1.5"><Truck className="w-3.5 h-3.5 text-emerald-300" /> Typically ships in 3–7 working days</span>
            <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-3.5 py-1.5"><PackageCheck className="w-3.5 h-3.5 text-emerald-300" /> UK £1.99 · International £4.99</span>
            <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/15 rounded-full px-3.5 py-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-300" /> Secure Stripe checkout</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        {/* Protocol Bundles */}
        {bundles.length > 0 && (
          <div className="mb-12" data-testid="store-bundles-section">
            <h2 className="text-base md:text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Percent className="w-5 h-5 text-primary" /> Protocol Bundles — save 10%
            </h2>
            <div className="grid md:grid-cols-2 gap-5">
              {bundles.map((b) => (
                <div key={b.id} className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 rounded-2xl p-6 shadow-sm" data-testid={`bundle-card-${b.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="font-semibold text-gray-900">{b.name}</p>
                      <p className="text-xs text-gray-500 mt-1">{b.tagline}</p>
                    </div>
                    <span className="bg-emerald-600 text-white text-[11px] font-bold rounded-full px-2.5 py-1 flex-shrink-0">SAVE £{b.savings_gbp.toFixed(2)}</span>
                  </div>
                  <div className="flex -space-x-2 my-3">
                    {b.items.slice(0, 7).map((i) => (
                      i.image_url && <img key={i.herb_id} src={i.image_url} alt={i.name} title={i.name} className="w-9 h-9 rounded-full border-2 border-white object-cover" />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mb-3">{b.items.map((i) => i.name).join(" · ")}</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-bold text-primary text-lg" data-testid={`bundle-price-${b.id}`}>£{b.price_gbp.toFixed(2)}</span>
                      <span className="text-xs text-gray-400 line-through ml-2">£{b.full_price_gbp.toFixed(2)}</span>
                    </div>
                    <button
                      onClick={() => { addItem(`bundle:${b.id}`); toast.success(`${b.name} added to cart`); }}
                      data-testid={`bundle-add-btn-${b.id}`}
                      className="inline-flex items-center gap-1.5 bg-primary text-white text-xs font-semibold rounded-full px-4 py-2 hover:bg-primary/90 transition-colors">
                      <ShoppingBag className="w-3.5 h-3.5" /> Add Bundle
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-3 md:items-center mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${products.length} herbs…`}
              data-testid="store-search-input"
              className="w-full h-11 pl-11 pr-4 rounded-full border border-gray-200 focus:outline-none focus:border-primary"
            />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="store-sort-select"
                  className="h-11 px-4 rounded-full border border-gray-200 bg-white text-sm focus:outline-none focus:border-primary">
            {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>

        <div className="flex gap-2 flex-wrap mb-8">
          {categories.map((c) => (
            <button key={c} onClick={() => setCategory(c)} data-testid={`store-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${category === c ? "bg-primary text-white border-primary" : "bg-white text-gray-600 border-gray-200 hover:border-primary"}`}>
              {c}
            </button>
          ))}
        </div>

        {shown.length === 0 ? (
          <p className="text-gray-500 text-center py-16" data-testid="store-no-results">No herbs match your search.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {shown.map((p) => (
              <div key={p.id} className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group" data-testid={`store-card-${p.id}`}>
                <Link to={`/herbs/${p.id}`} className="block relative">
                  <div className="aspect-square overflow-hidden bg-emerald-50">
                    {p.image_url && <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />}
                  </div>
                  <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
                  {p.category && (
                    <span className="absolute bottom-3 left-3 text-[10px] font-semibold text-white/95 bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-2.5 py-1">
                      {p.category}
                    </span>
                  )}
                </Link>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <Link to={`/herbs/${p.id}`} className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate group-hover:text-primary transition-colors">{p.name}</p>
                      <p className="text-[11px] text-gray-400 italic truncate">{p.scientific_name}</p>
                    </Link>
                    {p.amazon_url && (
                      <a href={p.amazon_url} target="_blank" rel="noopener noreferrer"
                         onClick={(e) => e.stopPropagation()}
                         title="View on Amazon (external retailer — sold and fulfilled by Amazon)"
                         data-testid={`store-amazon-link-${p.id}`}
                         className="flex-shrink-0 text-[10px] text-gray-400 hover:text-amber-600 inline-flex items-center gap-0.5 mt-0.5 transition-colors">
                        Amazon <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  <p className="text-[11px] text-emerald-700 mt-1.5 flex items-center gap-1">
                    <PackageCheck className="w-3 h-3" /> In stock · typically ships in 3–7 working days
                  </p>
                  {p.review_count > 0 && (
                    <p className="text-[11px] text-gray-600 mt-1 flex items-center gap-1" data-testid={`store-card-rating-${p.id}`}>
                      <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                      <b>{p.avg_rating}</b> ({p.review_count})
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-3">
                    <span className="font-bold text-primary" data-testid="store-card-price">£{p.price_gbp.toFixed(2)}</span>
                    <button
                      onClick={() => { addItem(p.id); toast.success(`${p.name} added to cart`); }}
                      data-testid="store-card-add-btn"
                      className="inline-flex items-center gap-1.5 bg-primary text-white text-xs font-semibold rounded-full px-3 py-1.5 hover:bg-primary/90 active:scale-95 transition-all">
                      <ShoppingBag className="w-3.5 h-3.5" /> Add
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
